// Hỏi giá đối tác / đại lý (Mint-RateInquiry): trạng thái yêu cầu, so sánh giá chào, điểm đại lý.
import type { AgentScore, Inquiry, InquiryAgent, InquiryQuote, InternalInquiry } from './types';

export type InquiryStatus = 'draft' | 'wait' | 'mine' | 'priced' | 'nores' | 'won';

export const INQUIRY_STATUS: Record<InquiryStatus, { label: string; tone: 'neutral' | 'info' | 'warn' | 'ok' | 'err' }> = {
  draft: { label: 'Nháp', tone: 'neutral' },
  wait: { label: 'Chờ đối tác', tone: 'info' },
  mine: { label: 'Chờ mình', tone: 'warn' },
  priced: { label: 'Có giá', tone: 'ok' },
  nores: { label: 'Chưa ai trả lời', tone: 'err' },
  won: { label: 'Đã chọn', tone: 'ok' },
};

export const INQUIRY_FILTERS: readonly { key: 'all' | Exclude<InquiryStatus, 'nores'>; label: string }[] = [
  { key: 'all', label: 'Tất cả' },
  { key: 'draft', label: 'Nháp' },
  { key: 'wait', label: 'Chờ đối tác' },
  { key: 'mine', label: 'Chờ mình' },
  { key: 'priced', label: 'Có giá' },
  { key: 'won', label: 'Đã chọn' },
];

/** Trạng thái yêu cầu: nháp / đã chọn theo `base`; còn lại suy từ trả lời của đối tác. */
export function inquiryStatus(q: Pick<Inquiry, 'base'>, agents: readonly Pick<InquiryAgent, 'status' | 'draft'>[]): InquiryStatus {
  if (q.base === 'draft') return 'draft';
  if (q.base === 'won') return 'won';
  const priced = agents.filter((a) => a.status === 'q').length;
  if (q.base === 'nores' && priced === 0) return 'nores';
  if (agents.some((a) => a.status === 'q' && a.draft)) return 'mine';
  return priced ? 'priced' : 'wait';
}

/** Số đối tác đã trả lời (có giá hoặc từ chối) / số đã gửi. */
export function replyCount(q: Pick<Inquiry, 'sent'>, agents: readonly Pick<InquiryAgent, 'status'>[]): { replied: number; sent: number; pct: number } {
  const replied = agents.filter((a) => a.status !== 'p').length;
  return { replied, sent: q.sent, pct: q.sent ? Math.round((replied / q.sent) * 100) : 0 };
}

export interface CompareRow {
  name: string;
  usd: number;
  /** chênh so với giá chào rẻ nhất, % (làm tròn 1 chữ số) */
  deltaPct: number;
  /** độ dài thanh 0–100 so với giá chào cao nhất */
  bar: number;
  cheapest: boolean;
}

/** So sánh giá chào: chỉ đối tác có giá (status q); cần quyền `money` để có `quotes`. */
export function compareQuotes(agents: readonly InquiryAgent[], quotes: readonly InquiryQuote[]): CompareRow[] {
  const byId = new Map(quotes.map((q) => [q.id, q]));
  const rows = agents.flatMap((a) => {
    const q = a.status === 'q' ? byId.get(`${a.inquiry}|${a.idx}`) : undefined;
    return q ? [{ name: a.name, usd: q.usd }] : [];
  });
  if (!rows.length) return [];
  const min = Math.min(...rows.map((r) => r.usd));
  const max = Math.max(...rows.map((r) => r.usd));
  return rows
    .map((r) => ({ ...r, deltaPct: Math.round(((r.usd - min) / min) * 1000) / 10, bar: Math.round((r.usd / max) * 100), cheapest: r.usd === min }))
    .sort((a, b) => a.usd - b.usd);
}

/** Điểm đại lý: 40% tỷ lệ trả lời + 30% tỷ lệ rẻ nhất + 30% tỷ lệ được chọn. */
export function agentPoints(s: Pick<AgentScore, 'received' | 'replyRate' | 'cheapest' | 'won'>): number {
  if (s.received <= 0) return 0;
  return Math.round(s.replyRate * 0.4 + Math.min(s.cheapest / s.received, 1) * 30 + Math.min(s.won / s.received, 1) * 30);
}

export const ranked = (scores: readonly AgentScore[]): (AgentScore & { points: number })[] =>
  scores.map((s) => ({ ...s, points: agentPoints(s) })).sort((a, b) => b.points - a.points);

export const INTERNAL_FILTERS: readonly { key: 'open' | InternalInquiry['status'] | 'all'; label: string; test: (r: InternalInquiry) => boolean }[] = [
  { key: 'open', label: 'Đang mở', test: (r) => r.status === 'Asked' || r.status === 'Taken' },
  { key: 'Quoted', label: 'Đã có giá', test: (r) => r.status === 'Quoted' },
  { key: 'Skip', label: 'Bỏ qua', test: (r) => r.status === 'Skip' },
  { key: 'all', label: 'Tất cả', test: () => true },
];

export const INTERNAL_STATUS: Record<InternalInquiry['status'], string> = { Asked: 'Đã hỏi', Taken: 'Đang làm', Quoted: 'Đã có giá', Skip: 'Bỏ qua' };

// ── Tạo yêu cầu hỏi giá (ngăn kéo, board `dw`) ────────────────────────────────
export type InquirySvc = 'FRT' | 'TRF' | 'DDL' | 'ISR' | 'DOC';
export type InquiryLang = 'VI' | 'EN';

export const INQUIRY_SVC: readonly { key: InquirySvc; label: string }[] = [
  { key: 'FRT', label: 'Cước FRT' },
  { key: 'TRF', label: 'Vận tải TRF' },
  { key: 'DDL', label: 'Hải quan DDL' },
  { key: 'ISR', label: 'Bảo hiểm ISR' },
  { key: 'DOC', label: 'Chứng từ DOC' },
];

/** Trường của phiếu theo dịch vụ: [nhãn, giá trị mẫu]. */
export const INQUIRY_FIELDS: Record<InquirySvc, readonly (readonly [string, string])[]> = {
  FRT: [['POL', 'VNSGN Hồ Chí Minh'], ['POD', 'KRPUS Busan'], ['Loại cont × SL', '2 × 40RH'], ['Hàng', 'Thuỷ sản đông lạnh · −18 °C'], ['ETD dự kiến', 'Tuần 45'], ['Hạn trả lời', '24/Oct 17:00']],
  TRF: [['Điểm lấy', 'Cát Lái'], ['Điểm giao', 'KCN VSIP 1'], ['Loại xe / cont', '40RH cắm điện'], ['Số chuyến', '3'], ['Ngày chạy', '25/Oct'], ['Hạn trả lời', '23/Oct 16:00']],
  DDL: [['Cửa khẩu', 'Cát Lái · 02CI'], ['Loại hình', 'Nhập kinh doanh'], ['Số tờ khai', '1'], ['Hàng', 'Linh kiện'], ['Ngày', '24/Oct'], ['Hạn trả lời', '23/Oct 17:00']],
  ISR: [['Hàng', 'Thuỷ sản'], ['Giá trị', 'theo hoá đơn'], ['Tuyến', 'VNSGN → KRPUS'], ['Điều kiện', 'ICC (A)'], ['Ngày', 'Tuần 45'], ['Hạn trả lời', '24/Oct']],
  DOC: [['Loại chứng từ', 'C/O form AK'], ['Số bộ', '1'], ['Thị trường', 'Hàn Quốc'], ['Hàng', 'Thuỷ sản'], ['Ngày cần', '25/Oct'], ['Hạn trả lời', '23/Oct']],
};

/** Đối tác gợi ý để chọn gửi (theo board). `SUGGEST` = 4 đối tác từng chào tuyến tương tự. */
export const INQUIRY_PARTNERS = ['SAE MYUNG SEA AND AIR', 'TIANJIN BLUE OCEAN LOG.', 'ORIENT STAR LOGISTICS', 'JIN HAI SHIPPING AGENCY', 'PAN ASIA LOGISTICS', 'VTHAILONG', 'PHUONG NAM TRANS', 'TAN CANG TRUCKING', 'HAI QUAN SAI GON SERVICE', 'CUSTOMS PRO VN'] as const;
export const INQUIRY_SUGGEST: readonly number[] = [0, 1, 2, 3];

/** Tiêu đề + nội dung thư gửi đối tác (xem trước). */
export function inquiryMail(svc: InquirySvc, lang: InquiryLang, fields: readonly (readonly [string, string])[]): { subject: string; body: string } {
  const en = lang === 'EN';
  const [f0, f1] = [fields[0]?.[1] ?? '', fields[1]?.[1] ?? ''];
  const subject = `${en ? 'RATE INQUIRY' : 'HỎI GIÁ'} · ${svc} · ${f0} → ${f1} · HANA LOGISTICS`;
  const head = en ? 'Dear partner,\nPlease quote your best rate for:\n' : 'Kính gửi quý đối tác,\nVui lòng báo giá tốt nhất cho:\n';
  const tail = en ? '\nBest regards, Pricing team' : '\nTrân trọng, Bộ phận Pricing';
  return { subject, body: head + fields.map(([k, v]) => `• ${k}: ${v}`).join('\n') + tail };
}
